import styled from "styled-components";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import {
  OnboardingButtonOutlined,
  OnboardingButtonPrimary,
} from "../components/OnboardingButton";
import {
  OnboardingInput,
  TooltipText,
  TooltipWrapper,
} from "../components/OnboardingInput";
import OnboardingHelpLink from "../components/OnboardingHelpLink";
import { HeadingLarge, LabelMedium } from "../components/OnboardingTypography";
import { useState, useEffect, useMemo } from "react";
import IconTooltipHelp from "../icons/IconTooltipHelp";
import { 
  FlexColumn, 
  FlexRow, 
  MessageBox, 
  FormGrid, 
  EmptyGridBlock,
  Spinner,
  THEME
} from "../components/OnboardingLayoutComponents";
import { useErrorHandler } from "../hooks/useErrorHandler";
import { ERROR_CONTEXTS } from "../utils/errorMessages";
import { messages } from "../onboarding.i18n";
import { VALIDATION_PATTERNS, VALIDATION_RULES } from "../utils/validationPatterns";

// Removed - now using FormGrid and EmptyGridBlock from OnboardingLayoutComponents

const OnboardingPage2 = ({
  userName,
  setUserName,
  email,
  setEmail,
  phoneNumber,
  setPhoneNumber,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  goToNextStep,
  goToPreviousStep,
  isLoading,
  savedOk,
  pageHasError,
}) => {
  const [errors, setErrors] = useState({});
  
  // Use centralized error handling for registration errors
  const errorHandler = useErrorHandler(ERROR_CONTEXTS.REGISTRATION);
  
  // Sync parent error state with centralized error handler
  useEffect(() => {
    if (pageHasError && typeof pageHasError === 'string') {
      errorHandler.setCustomError(pageHasError, 'error');
    } else if (pageHasError && typeof pageHasError === 'object') {
      // If pageHasError is an error object, handle it properly
      errorHandler.handleError(pageHasError, ERROR_CONTEXTS.REGISTRATION);
    } else if (!pageHasError) {
      errorHandler.clearError();
    }
  }, [pageHasError]);

  const validateField = (fieldName, value) => {
    const newErrors = { ...errors };

    switch (fieldName) {
      case "userName":
        if (!value.trim()) {
          newErrors.userName = messages.nb.userNameRequired;
        } else if (value.trim().length < VALIDATION_RULES.USERNAME_MIN_LENGTH) {
          newErrors.userName = messages.nb.userNameMinLength;
        } else {
          delete newErrors.userName;
        }
        break;
      case "email":
        if (!value.trim()) {
          newErrors.email = messages.nb.emailRequired;
        } else if (!VALIDATION_PATTERNS.EMAIL.test(value.trim())) {
          newErrors.email = messages.nb.emailInvalid;
        }
        else {
          delete newErrors.email;
        }
        break;
      case "phoneNumber":
        if (!value.trim()) {
          newErrors.phoneNumber = messages.nb.phoneRequired;
        } else if (!VALIDATION_PATTERNS.PHONE_NO.test(value.trim().replace(/\s/g, ''))) {
          newErrors.phoneNumber = messages.nb.phoneInvalid;
        } else {
          delete newErrors.phoneNumber;
        }
        break;
      case "password":
        if (!value.trim()) {
          newErrors.password = messages.nb.passwordRequired;
        } else if (!VALIDATION_PATTERNS.PASSWORD.test(value)) {
          newErrors.password = messages.nb.passwordMinLength;
        } else {
          delete newErrors.password;
        }
        break;
      case "confirmPassword":
        if (value !== password) {
          newErrors.confirmPassword = messages.nb.confirmPasswordMismatch;
        } else {
          delete newErrors.confirmPassword;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  // Validate all fields at once (for submission)
  const validateAllFields = () => {
    validateField("userName", userName);
    validateField("email", email);
    validateField("phoneNumber", phoneNumber);
    validateField("password", password);
    validateField("confirmPassword", confirmPassword);
  };

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    // All required fields must have non-whitespace values
    const hasValidUserName = userName?.trim().length > 0;
    const hasValidEmail = email?.trim().length > 0;
    const hasValidPhone = phoneNumber?.trim().length > 0;
    const hasValidPassword = password?.trim().length > 0;
    const hasValidConfirmPassword = confirmPassword?.trim().length > 0;

    const hasAllRequiredFields =
      hasValidUserName &&
      hasValidEmail &&
      hasValidPhone &&
      hasValidPassword &&
      hasValidConfirmPassword;

    // Passwords must match (explicit check, not relying on blur-triggered validation)
    const passwordsMatch = password === confirmPassword;

    // No validation errors present
    const hasNoErrors = Object.keys(errors).length === 0;

    return hasAllRequiredFields && hasNoErrors && passwordsMatch;
  }, [userName, email, phoneNumber, password, confirmPassword, errors]);
  return (
    <div>
      <FlexColumn gap={THEME.spacing.xxxl} padding={"0 0 " + THEME.spacing.mega + " 0"}>
        <HeadingLarge style={{ paddingTop: THEME.spacing.xxl }}>
          <FormattedMessage id="step2Title" />
        </HeadingLarge>
        <FlexColumn gap={THEME.spacing.xxl}>
          <LabelMedium>
            <FormattedMessage id="step2Description1" />
          </LabelMedium>
          <LabelMedium>
            <FormattedMessage id="step2Description2" />
          </LabelMedium>
          <OnboardingHelpLink />
        </FlexColumn>

      <form onSubmit={(e) => {
        e.preventDefault();
        // Only proceed if form is valid (isFormValid memo already checks all conditions)
        if (isFormValid && !isLoading) {
          goToNextStep();
        }
      }}>
        <FormGrid columnGap={THEME.spacing.ultra} rowGap={THEME.spacing.xxxl}>
          <OnboardingInput
            label={<FormattedMessage id="userNameLabel" />}
            value={userName}
            autoComplete="name"
            onChange={(e) => setUserName(e.target.value)}
            onBlur={() => validateField("userName", userName)}
            error={errors.userName}
          />
          <EmptyGridBlock />
          <OnboardingInput
            label={<FormattedMessage id="emailLabel" />}
            value={email}
            type="email"
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => validateField("email", email)}
            error={errors.email}
          />
          <OnboardingInput
            label={<FormattedMessage id="phoneLabel" />}
            value={phoneNumber}
            type="tel"
            autoComplete="tel"
            onChange={(e) => setPhoneNumber(e.target.value)}
            onBlur={() => validateField("phoneNumber", phoneNumber)}
            error={errors.phoneNumber}
          />
          <OnboardingInput
            label={
              <FlexRow alignItems="center" gap={THEME.spacing.sm}>
                <FormattedMessage id="passwordLabel" />
                <TooltipWrapper>
                  <IconTooltipHelp width={24} height={24} />
                  <TooltipText>
                    <FormattedMessage id="passwordTooltip" />
                  </TooltipText>
                </TooltipWrapper>
              </FlexRow>
            }
            value={password}
            type="password"
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => validateField("password", password)}
            error={errors.password}
          />
          <OnboardingInput
            label={<FormattedMessage id="confirmPasswordLabel" />}
            value={confirmPassword}
            type="password"
            autoComplete="new-password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            onBlur={() => validateField("confirmPassword", confirmPassword)}
            error={errors.confirmPassword}
          />
        </FormGrid>

        {/* Validation status for screen readers */}
      <div
        id="form-validation-status"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {!isFormValid && Object.keys(errors).length > 0 && (
          <FormattedMessage
            id="formValidationErrors"
            defaultMessage="{count} {count, plural, one {felt} other {felter}} må rettes før du kan fortsette"
            values={{ count: Object.keys(errors).length }}
          />
        )}
        {!isFormValid && Object.keys(errors).length === 0 && password !== confirmPassword && (
          <FormattedMessage
            id="passwordsMustMatch"
            defaultMessage="Passordene må være like"
          />
        )}
      </div>

        <FlexRow gap={THEME.spacing.lg}>
          <OnboardingButtonOutlined type="button" onClick={goToPreviousStep}>
            <LabelMedium>
              <FormattedMessage id="previousButton" />
            </LabelMedium>
          </OnboardingButtonOutlined>
          <OnboardingButtonPrimary
            type="submit"
            disabled={!isFormValid || isLoading}
            aria-disabled={!isFormValid || isLoading}
            aria-describedby={!isFormValid ? "form-validation-status" : undefined}
          >
            <LabelMedium>
              <FormattedMessage id="nextButton" />
            </LabelMedium>
          </OnboardingButtonPrimary>
        </FlexRow>
      </form>
      <FlexRow>
        {isLoading && (
          <FlexRow alignItems="center" gap={THEME.spacing.sm}>
            <Spinner />
            <LabelMedium>
              <FormattedMessage id="loadingText" />
            </LabelMedium>
          </FlexRow>
        )}
        {savedOk && (
          <MessageBox variant="success">
            <FormattedMessage id="userCreatedSuccess" />
          </MessageBox>
        )}
        {errorHandler.hasError && (
          <FlexColumn gap={THEME.spacing.md}>
            <MessageBox {...errorHandler.getMessageBoxProps()} />
            <Link
              to="/login"
              style={{
                color: THEME.colors.primaryDark,
                textDecoration: 'underline',
                fontSize: THEME.fontSize.md
              }}
            >
              <FormattedMessage id="goToLogin" />
            </Link>
          </FlexColumn>
        )}
      </FlexRow>
      </FlexColumn>
    </div>
  );
};

export default OnboardingPage2;
